<template>
  <div class="audio-player shadow-dropdown p-5 z-1000 flex items-center">
    <button v-if="state === 'playing'" class="ml-1 audio-player__btn" @click="toggleAudio(false)">
      <i-pause-filled />
    </button>
    <button v-else class="ml-1 audio-player__btn" :disabled="state === 'loading'" @click="toggleAudio(true)">
      <i-play-filled />
    </button>
    <div class="relative mx-4 flex flex-wrap justify-between text-grey text-11 leading-20 w-260px">
      <vx-slider
        v-model="trackPosition"
        class="audio-player__slider"
        color="secondary"
        :max="getDuration(audioDuration)"
        :mark-format="formatTime"
        :disabled="state === 'loading'"
        @changed="seek"
        @input="handleSeekState"
      />
      <p>{{ formatTime(trackPosition) }}</p>
      <p>{{ formatTime(audioDuration) }}</p>
    </div>
    <a
      class="audio-player__btn p-1 mr-2"
      download
      :disabled="state === 'loading'"
      target="_blank"
      :href="record"
    >
      <i-download />
    </a>
    <button class="audio-player__btn p-1" @click="closeModal">
      <i-close />
    </button>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { Howl } from 'howler';
import debounce from 'lodash.debounce';

import IPlayFilled from '@/assets/svg/icons/play-filled.svg';
import IPauseFilled from '@/assets/svg/icons/pause-filled.svg';
import IDownload from '@/assets/svg/icons/download.svg';
import IClose from '@/assets/svg/icons/close-big.svg';

export default {
  name:       'AudioPlayerModal',
  components: {
    IPlayFilled,
    IPauseFilled,
    IDownload,
    IClose,
  },
  data() {
    return {
      trackPosition: 0,
      audioDuration: 0,
      soundAdapter:  null,
      watcher:       null,
      audioCtx:      null,
      isSeek:        false,
    };
  },
  computed: {
    ...mapGetters({
      record:   'audioPlayer/record',
      state:    'audioPlayer/state',
      recordId: 'audioPlayer/id',
    }),
  },
  watch: {
    recordId() {
      this.changeRecord(this.record);
    },
    state(newState) {
      if (newState === 'pause') this.soundAdapter.pause();
      else if (newState === 'playing') this.soundAdapter.play();
    },
  },
  mounted() {
    this.initAudioAdapter();
  },
  methods: {
    /**
     * Иницилизируем Howler.js для управление аудио-треками
     */
    initAudioAdapter() {
      this.$store.dispatch('audioPlayer/setPlayerState', 'loading');
      window.watcherDuration = null;
      this.soundAdapter = new Howl({
        src:   this.record,
        html5: true,

        /**
         * Событие при успешном загрузке трека
         */
        onload: () => {
          this.audioDuration = this.soundAdapter.duration();
          this.$store.dispatch('audioPlayer/setPlayerState', 'playing');
        },

        /**
         * Событие при воспроизведение трека
         */
        onplay: () => {
        // получаем времю текущего трека каждые 100 мсек
          this.watcher = setInterval(() => {
            try {
              if (!this.isSeek) this.trackPosition = this.soundAdapter.seek();
            } catch (e) {
              window.clearInterval(this.watcher);
            }
          }, 100);
        },

        /**
         * Событие при паузе трека
         */
        onpause: () => {
          clearInterval(this.watcher);
        },

        /**
         * Событие при остановке трека
         */
        onstop: () => {
          clearInterval(this.watcher);
          this.$store.dispatch('audioPlayer/setPlayerState', 'off');
        },

        /**
         * Событие при завершение воспроизведение трека
         */
        onend: () => {
          clearInterval(this.watcher);
          this.$store.dispatch('audioPlayer/setPlayerState', 'off');
        },
      });
    },

    /**
     * Форматирует время типа 00:00 для плеера
     *
     * @param {Number} secs - время указанное в секундах
     *
     * @return {string}
     */
    formatTime(secs) {
      if (!secs || secs === Infinity) return '00:00';

      const minutes = Math.floor(secs / 60) || 0;
      const seconds = Math.floor(secs - minutes * 60) || 0;

      return `${minutes}:${ seconds < 10 ? '0' : '' }${seconds}`;
    },

    /**
     * Play/Pause плеера
     *
     * @param {Boolean} bool
     */
    toggleAudio(bool) {
      if (this.state === 'loading') return;

      this.$store.dispatch('audioPlayer/setPlayerState', bool ? 'playing' : 'pause');
    },

    /**
     * Возвращает длительность запися, если он есть
     * Противном случае вернет 600 сек, т.е. 10 минут
     *
     * @param {Number} secs - время указанное в секундах
     *
     * @return {number}
     */
    getDuration(secs) {
      if (!secs || secs === Infinity) return 600;

      return Math.floor(secs);
    },

    /**
     * Задает состояние перемотки.
     * В момент, когда юзер через drag-drop передивигает ползунок, чтобы музыка не прервался и не перематался
     */
    handleSeekState() {
      this.isSeek = true;
    },

    /**
     * Перемотка запися.
     *
     * @param {Number} to - на какой секунду перемотаться, slider-input вернет значение
     */
    seek: debounce(function (to) {
      this.isSeek = false;
      this.soundAdapter.seek(to);
    }, 250),

    /**
     * Сменитить запись
     *
     * @param {String} src - источник записи. Пример https://examole.com/audio.mp3
     */
    changeRecord(src) {
      if (!this.soundAdapter) return;

      this.soundAdapter.unload();
      // eslint-disable-next-line no-underscore-dangle
      this.soundAdapter._src = src;
      this.trackPosition = 0;
      setTimeout(() => {
        this.$store.dispatch('audioPlayer/setPlayerState', 'loading');
        this.soundAdapter.load();
      }, 10);
    },

    /**
     * Закрыть модалку
     */
    closeModal() {
      this.soundAdapter.unload();
      this.$store.dispatch('audioPlayer/toggle', false);
      this.$store.dispatch('audioPlayer/setPlayerState', 'off');
    },
  },
};
</script>
